import { z } from 'zod';
/**
 * ehr#437 (PROGRESS_NOTE_10-14-V81 Quick add, QUICK_ADD_SETTINGS_SPEC_10-10-V1): sections a
 * clinician drops into one note from the Quick add card (a rating, a checklist, a box), or has on
 * every note for one client or for all their clients (Quick add settings, "On note"). Built-in
 * items, plus each person's own. The note keeps a copy of each section it was given (`quick`), so a
 * later change to the settings never changes a written note; the answers sit in `extras` by id.
 */
export const QUICK_GROUPS = ['ratings', 'checklists', 'boxes'];
export const QUICK_GROUP_LABEL = {
    ratings: 'Ratings',
    checklists: 'Checklists',
    boxes: 'Boxes',
};
/** Where a section goes: after Plan or Assessment, after the first box, or after the box the
 * phrases go in (Objective; Data on DAP; Intervention on BIRP). Ratings go after Session details. */
export const QUICK_AFTER = ['plan', 'assessment', '@first', '@phrase'];
export const quickItemSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9_]{1,31}$/),
    group: z.enum(QUICK_GROUPS),
    label: z.string().trim().min(1).max(60),
    /** A checklist's items. */
    items: z.array(z.string().trim().min(1).max(120)).max(15).optional(),
    /** A rating's scale. */
    min: z.number().int().min(0).max(10).optional(),
    max: z.number().int().min(1).max(10).optional(),
    after: z.enum(QUICK_AFTER).optional(),
});
const it = (group, id, label, extra = {}) => ({ id, group, label, ...extra });
const scale = { min: 0, max: 10 };
/** V81 QA_BUILTIN (ratings, checklists, boxes) and its practice starters. */
export const QUICK_BUILTIN = [
    ...[
        ['mood', 'Mood'],
        ['anxiety', 'Anxiety'],
        ['sleep', 'Sleep'],
        ['stress', 'Stress'],
        ['energy', 'Energy'],
        ['pain', 'Pain'],
        ['irritability', 'Irritability'],
        ['focus', 'Focus'],
        ['appetite', 'Appetite'],
        ['craving', 'Craving'],
        ['hopefulness', 'Hopefulness'],
    ].map(([k, l]) => it('ratings', `r_${k}`, l, scale)),
    // FAMILY_COUPLES_NOTE (65 A): offered on a couples note only
    it('ratings', 'r_relsat', 'Relationship satisfaction', scale),
    it('ratings', 'r_session', 'Session', { min: 1, max: 5 }),
    it('checklists', 'c_mental', 'Mental status', {
        items: [
            'Alert and oriented x3',
            'Appearance appropriate',
            'Speech normal rate and volume',
            'Mood and affect congruent',
            'Thought process logical',
            'Insight and judgment fair',
        ],
        after: '@phrase',
    }),
    it('checklists', 'c_homework', 'Homework', {
        items: [
            'Practiced the skill between sessions',
            'Completed the worksheet',
            'Tracked symptoms in the log',
        ],
        after: 'plan',
    }),
    it('checklists', 'c_coping', 'Coping skills used', {
        items: [
            'Deep breathing',
            'Grounding',
            'Journaling',
            'Physical activity',
            'Reached out to support',
            'Thought challenging',
        ],
        after: '@phrase',
    }),
    it('checklists', 'c_protective', 'Protective factors', {
        items: [
            'Family support',
            'Engaged in treatment',
            'Reasons for living',
            'Stable housing',
            'Employment or school',
            'Faith or community',
        ],
        after: 'assessment',
    }),
    it('checklists', 'c_substance', 'Substance use check', {
        items: [
            'No use reported',
            'Alcohol use reported',
            'Cannabis use reported',
            'Other substance use reported',
            'Cravings reported',
        ],
        after: '@phrase',
    }),
    it('checklists', 'c_medadh', 'Medication adherence', {
        items: ['Taking as prescribed', 'Missed doses', 'Side effects reported', 'Refill needed'],
        after: 'plan',
    }),
    it('checklists', 'c_sleephyg', 'Sleep hygiene', {
        items: ['Consistent bedtime', 'No screens before bed', 'Limited caffeine', 'Wind-down routine'],
        after: 'plan',
    }),
    it('checklists', 'c_safetyplan', 'Safety plan review', {
        items: [
            'Reviewed warning signs',
            'Reviewed coping strategies',
            'Confirmed support contacts',
            'Confirmed crisis line numbers',
            'Means safety discussed',
        ],
        after: 'plan',
    }),
    it('boxes', 'b_meds', 'Medication changes', { after: 'plan' }),
    it('boxes', 'b_strengths', 'Client strengths', { after: 'assessment' }),
    it('boxes', 'b_coord', 'Coordination of care', { after: 'plan' }),
    it('boxes', 'b_barriers', 'Barriers to progress', { after: 'assessment' }),
    it('boxes', 'b_collateral', 'Collateral information', { after: '@first' }),
    it('boxes', 'b_crisisplan', 'Crisis plan review', { after: 'plan' }),
    it('boxes', 'b_discharge', 'Progress toward discharge', { after: 'plan' }),
];
/**
 * ehr#682 (PROGRESS-NOTE-10-20-V141 V119, V130): the rail cards Note settings > Quick add can hide,
 * each with its own "Show ..." On / Off switch, saved for this client or all clients with the Quick
 * add lists. V141's Side cards list has Generate note; it is the live Generate Notes card.
 */
export const QUICK_SIDE_CARDS = [
    ['measures', 'Measures'],
    ['last', 'Last session'],
    ['changes', 'My changes'],
    ['gen', 'Generate Notes'],
];
const QUICK_SIDE_CARD_KEYS = QUICK_SIDE_CARDS.map(([k]) => k);
const sideCardList = z.array(z.enum(QUICK_SIDE_CARD_KEYS)).max(QUICK_SIDE_CARDS.length);
/**
 * ehr#728 (COLLATERAL_CONTACT_NOTE_10-30-V5 Side cards): one note kind's own rail cards switched
 * off (a Collateral contact note's Side cards tab), apart from the Quick add tab's switches: all
 * clients, and one client's own (replaces all clients). V5 kept them in the note's own storage.
 */
const kindCardsSchema = z.object({
    all: sideCardList.optional(),
    clients: z
        .record(z.string().uuid(), sideCardList)
        .refine((r) => Object.keys(r).length <= 500, { message: 'Too many clients.' })
        .optional(),
});
/** A person's Quick add settings (users."notePrefs".quickAdd). */
export const quickPrefsSchema = z.object({
    /** On every note, all clients. */
    onNote: z.array(z.string().max(32)).max(40).default([]),
    /** On every note for one client (replaces onNote for that client). */
    clients: z
        .record(z.string().uuid(), z.array(z.string().max(32)).max(40))
        .refine((r) => Object.keys(r).length <= 500, { message: 'Too many clients.' })
        .default({}),
    /** Their own items ("+ Add your own"); ids start with my_. */
    mine: z
        .array(quickItemSchema.refine((i) => i.id.startsWith('my_'), { message: 'Own items start my_' }))
        .max(30)
        .default([]),
    /** ehr#478 (V81 Quick add settings): each group's own order (dragged), ids first to last. */
    order: z.record(z.enum(QUICK_GROUPS), z.array(z.string().max(32)).max(80)).optional(),
    /**
     * ehr#478, ehr#682 (V141 V129): Show Quick add is Off (the card is off the note rail). Note
     * settings > Quick add turns it back on. It was the Hide Quick add button: same key, so a card
     * hidden before reads as Show Quick add Off.
     */
    hidden: z.boolean().optional(),
    /** ehr#682 (V141 V130): the rail cards switched off, all clients. */
    cardsHidden: sideCardList.optional(),
    /** ehr#682: the same for one client, saved with that client's own list (replaces cardsHidden). */
    clientCards: z
        .record(z.string().uuid(), sideCardList)
        .refine((r) => Object.keys(r).length <= 500, { message: 'Too many clients.' })
        .optional(),
    /** ehr#728: the Side cards of a note kind with no Quick add, by kind (Collateral contact). */
    kindCards: z
        .record(z.string().min(1).max(40), kindCardsSchema)
        .refine((r) => Object.keys(r).length <= 20, { message: 'Too many note kinds.' })
        .optional(),
});
export const EMPTY_QUICK_PREFS = { onNote: [], clients: {}, mine: [] };
/**
 * ehr#522 (Jay 10-18, "default off all quick adds"): someone who has never saved Quick add
 * settings starts with the card off the note rail (Show Quick add in Note settings > Quick add
 * brings it back and saves that) and nothing on any note. Saved settings are read as saved.
 */
export const DEFAULT_QUICK_PREFS = { ...EMPTY_QUICK_PREFS, hidden: true };
/** Read stored settings, dropping anything that no longer fits; none saved yet reads as the default. */
export function readQuickPrefs(v) {
    if (v === undefined || v === null)
        return { ...DEFAULT_QUICK_PREFS };
    const r = quickPrefsSchema.safeParse(v);
    return r.success ? r.data : EMPTY_QUICK_PREFS;
}
/** Every item a person can add: built-in, then their own. */
export function quickLibrary(prefs) {
    return [...QUICK_BUILTIN, ...prefs.mine];
}
/**
 * ehr#478 (V81 quickLibrary): one group's items in the person's order (dragged in Quick add
 * settings), then any not in it in library order.
 */
export function quickOrdered(prefs, group) {
    const all = quickLibrary(prefs).filter((i) => i.group === group);
    const ord = prefs.order?.[group] ?? [];
    const at = (i) => {
        const k = ord.indexOf(i.id);
        return k < 0 ? 1e6 + all.indexOf(i) : k;
    };
    return [...all].sort((a, b) => at(a) - at(b));
}
/**
 * ehr#682: the rail cards off for this client: theirs when they have settings of their own (saved
 * before the switches existed: the all-clients ones), else the all-clients ones.
 */
export function cardsHiddenFor(prefs, contactId) {
    const own = prefs.clients[contactId] ? prefs.clientCards?.[contactId] : undefined;
    return own ?? prefs.cardsHidden ?? [];
}
/**
 * ehr#728: the rail cards off on a note kind with its own Side cards (a Collateral contact note):
 * this client's own, else the all-clients ones, else none (every card on). The Quick add tab's
 * switches never reach it, and it never reaches the other notes.
 */
export function kindCardsHiddenFor(prefs, contactId, kind) {
    const k = prefs.kindCards?.[kind];
    return k?.clients?.[contactId] ?? k?.all ?? [];
}
/** The items on every note for this client: the client's own list, else the all-clients one. */
export function quickOnNoteFor(prefs, contactId) {
    const ids = prefs.clients[contactId] ?? prefs.onNote;
    const lib = quickLibrary(prefs);
    return ids.map((id) => lib.find((i) => i.id === id)).filter((i) => !!i);
}
/** One change on this note's My changes card, with what Undo puts back. */
export const myChangeSchema = z.object({
    at: z.string().max(40),
    kind: z.enum(['add', 'remove']),
    item: quickItemSchema,
    /** What was in a removed section. */
    value: z.union([z.string().max(20000), z.array(z.string().max(160)).max(30)]).optional(),
});
//# sourceMappingURL=quickAdd.js.map